import { TRANSACTION_TYPES } from '@shared/constants/transaction-types';

/**
 * Natural direction of an amount, independent of the sign the API sends.
 *
 * Several endpoints return magnitudes (outstanding balances, taxes) that are
 * debits by nature even though they arrive positive, so the direction has to
 * be stated by the screen instead of inferred from the sign.
 */
export type CashDirection = 'credit' | 'debit';

/**
 * CSS classes that paint an amount after its C/D indicator.
 *
 * Declared globally in `src/styles.scss`: credit resolves to `--cds-revenue`
 * (blue) and debit to `--cds-expense` (red).
 */
export const CASH_INDICATOR_CLASS: Record<CashDirection, string> = {
  credit: 'cash-credit',
  debit: 'cash-debit',
};

/**
 * Normalizes an amount to a number, treating null/undefined as zero.
 *
 * @param value Amount as sent by the API
 * @returns Numeric amount, 0 when absent or not a number
 */
function toNumber(value: number | null | undefined): number {
  return Number.isFinite(value) ? Number(value) : 0;
}

/**
 * Applies the direction's sign to an amount so `formatCash`/`MoneyPipe`
 * render the matching C (credit) / D (debit) indicator.
 *
 * Both formatters derive the indicator from the sign, so a debit that arrives
 * as a magnitude would otherwise be labelled C (card 86e37v532).
 *
 * @param value Amount as sent by the API, signed or as a magnitude
 * @param direction Natural direction of the amount; omitted keeps the sign
 * @returns Amount carrying the direction's sign
 * @example
 * signedCash(595, 'debit');  // -595 -> 'R$ 595,00 D'
 * signedCash(595, 'credit'); //  595 -> 'R$ 595,00 C'
 */
export function signedCash(value: number | null | undefined, direction?: CashDirection): number {
  const numerical = toNumber(value);

  if (!numerical || !direction) {
    return numerical;
  }

  return direction === 'debit' ? -Math.abs(numerical) : Math.abs(numerical);
}

/**
 * Resolves the color class for an amount from the indicator it will render.
 *
 * Keeps color and indicator in sync: C is always blue and D always red, no
 * matter which sign the endpoint used.
 *
 * @param value Amount as sent by the API, signed or as a magnitude
 * @param direction Natural direction of the amount; omitted uses the sign
 * @returns Class name, or an empty string for zero (rendered with no indicator)
 */
export function cashIndicatorClass(
  value: number | null | undefined,
  direction?: CashDirection,
): string {
  const numerical = signedCash(value, direction);

  if (!numerical) {
    return '';
  }

  return numerical < 0 ? CASH_INDICATOR_CLASS.debit : CASH_INDICATOR_CLASS.credit;
}

/**
 * Appends the C/D indicator to an already formatted amount.
 *
 * For values formatted outside the cash pipes (e.g. `Intl.NumberFormat` in the
 * detail modals), so the indicator still comes from the same source as the
 * color class.
 *
 * @param formatted Amount already formatted for display
 * @param value Raw amount, used only to resolve the indicator
 * @param direction Natural direction of the amount; omitted uses the sign
 * @returns Formatted amount with a trailing C/D, or unchanged when zero
 */
export function withCashIndicator(
  formatted: string,
  value: number | null | undefined,
  direction?: CashDirection,
): string {
  const numerical = signedCash(value, direction);

  if (!numerical) {
    return formatted;
  }

  const indicator = numerical < 0 ? TRANSACTION_TYPES.DEBIT : TRANSACTION_TYPES.CREDIT;

  return `${formatted} ${indicator}`;
}
